import { el } from "./dom.js";
import { magButton, MAG } from "./magazine/kit.js";
import { OVERRIDABLE_FIELDS, RESET, isOverridden, yearField } from "../utils/overrides.js";

// "Edit details" modal: title, year (+ release date for games) and cover, per
// media type. Resolves to a patch { field: value | RESET } holding only the fields
// that changed, or null when cancelled / nothing changed. The caller writes it via
// db/overrides.js setOverrides, which parks the catalog value so "↺ Catalog" (and
// the hero menu's reset) can bring it back.

const LABELS = {
	title: "Title",
	cover_url: "Cover image URL",
	release_year: "Year",
	published_year: "Year",
	premiered_year: "Year",
	release_date: "Release date",
};

function sameValue(a, b) {
	return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

export function showEditDetailsSheet({ mediaType, record, catalog = null }) {
	return new Promise((resolve) => {
		let settled = false;
		const finish = (value) => {
			if (settled) return;
			settled = true;
			overlay.remove();
			document.removeEventListener("keydown", onKey);
			resolve(value);
		};
		const onKey = (e) => {
			if (e.key === "Escape") finish(null);
		};
		document.addEventListener("keydown", onKey);

		const fields = OVERRIDABLE_FIELDS[mediaType] ?? [];
		const sv = record.source_values ?? {};
		const state = {}; // field -> { input, reset }
		const errorLine = el("div.modal__hint", { style: { color: "#ff5a4a", minHeight: "1em" } });

		function row(field, input, hint) {
			state[field] = { input, reset: false };
			const resetBtn = el(
				"button.btn.btn--small",
				{
					type: "button",
					hidden: !isOverridden(record, field),
					title: "Put this field back to what the catalog says",
					onclick: () => {
						input.value = sv[field] ?? "";
						state[field].reset = true;
						resetBtn.hidden = true;
						input.dispatchEvent(new Event("preview"));
					},
				},
				"↺ Catalog",
			);
			input.addEventListener("input", () => {
				state[field].reset = false;
				errorLine.textContent = "";
			});
			return el(
				"div.field",
				{},
				el(
					"div",
					{ style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" } },
					el("label.field__label", {}, LABELS[field] ?? field),
					resetBtn,
				),
				input,
				hint ? el("div.modal__hint", { style: { marginTop: "4px" } }, hint) : null,
			);
		}

		const body = el("div", {});

		// Title.
		if (fields.includes("title")) {
			body.append(row("title", el("input.field__select", { type: "text", value: record.title ?? "" })));
		}

		// Year (+ precise date for games).
		const yf = yearField(mediaType);
		if (fields.includes(yf)) {
			body.append(
				row(
					yf,
					el("input.field__select", {
						type: "text",
						inputmode: "numeric",
						placeholder: "e.g. 2027",
						value: record[yf] != null ? String(record[yf]) : "",
					}),
				),
			);
		}
		if (fields.includes("release_date")) {
			body.append(
				row(
					"release_date",
					el("input.field__select", { type: "date", value: record.release_date ?? "" }),
					"Leave empty for \"TBD\".",
				),
			);
		}

		// Cover: candidates + URL + preview.
		if (fields.includes("cover_url")) {
			const urlInput = el("input.field__select", {
				type: "url",
				placeholder: "https://…",
				value: record.cover_url ?? "",
			});
			const preview = el("img", {
				alt: "",
				style: {
					width: "60px", height: "80px", objectFit: "cover", borderRadius: "8px",
					background: MAG.bgGlass, border: `1px solid ${MAG.line}`, flex: "none",
				},
			});
			const paintPreview = () => {
				preview.src = urlInput.value.trim() || "";
				preview.style.opacity = urlInput.value.trim() ? "1" : ".3";
				for (const [url, thumb] of thumbs) {
					thumb.style.outline = url === urlInput.value.trim() ? `2px solid ${MAG.lime}` : "none";
				}
			};
			const candidates = [
				...new Set(
					[
						sv.cover_url ?? record.cover_url,
						...(record.owned_editions ?? []).map((e) => e?.cover_url),
						...(catalog?.seasons ?? []).map((s) => s?.cover_url),
					].filter(Boolean),
				),
			];
			const thumbs = new Map();
			const strip = el("div", {
				style: { display: "flex", gap: "8px", overflowX: "auto", padding: "6px 0 4px" },
			});
			for (const url of candidates) {
				const thumb = el("img", {
					src: url,
					alt: "",
					role: "button",
					title: url === (sv.cover_url ?? record.cover_url) ? "Source cover" : "Candidate cover",
					style: {
						width: "46px", height: "62px", objectFit: "cover", borderRadius: "8px",
						cursor: "pointer", flex: "none", outlineOffset: "2px",
					},
					onclick: () => {
						urlInput.value = url;
						state.cover_url.reset = false;
						paintPreview();
					},
				});
				thumbs.set(url, thumb);
				strip.append(thumb);
			}
			urlInput.addEventListener("input", paintPreview);
			urlInput.addEventListener("preview", paintPreview);
			const coverRow = row("cover_url", urlInput, candidates.length > 1 ? "Tap a candidate, or paste an image URL." : "Paste an image URL.");
			coverRow.append(
				el("div", { style: { display: "flex", gap: "12px", alignItems: "flex-start", marginTop: "6px" } }, preview, strip),
			);
			body.append(coverRow);
			paintPreview();
		}

		function collect() {
			const patch = {};
			for (const field of fields) {
				const entry = state[field];
				if (!entry) continue;
				if (entry.reset) {
					if (isOverridden(record, field)) patch[field] = RESET;
					continue;
				}
				const raw = entry.input.value.trim();
				let value;
				if (field === "title") {
					if (!raw) {
						errorLine.textContent = "A title can't be empty.";
						return null;
					}
					value = raw;
				} else if (field === "cover_url") {
					value = raw || null;
				} else if (field === "release_date") {
					if (raw && !/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
						errorLine.textContent = "Release date must be YYYY-MM-DD.";
						return null;
					}
					value = raw || null;
				} else {
					if (raw && !/^\d{4}$/.test(raw)) {
						errorLine.textContent = "Year must be four digits.";
						return null;
					}
					value = raw ? Number(raw) : null;
				}
				if (!sameValue(value, record[field] ?? null)) patch[field] = value;
			}
			return patch;
		}

		const overlay = el(
			"div.modal-overlay",
			{
				onclick: (e) => {
					if (e.target === overlay) finish(null);
				},
			},
			el(
				"div.modal",
				{ style: { maxHeight: "90vh", overflowY: "auto" } },
				el("h2.modal__title", {}, "Edit details"),
				el(
					"p.modal__hint",
					{},
					"Fields you edit stay as you set them — catalog refreshes won't overwrite them. ↺ Catalog puts one back.",
				),
				body,
				errorLine,
				el(
					"div.modal__actions",
					{ style: { padding: "0" } },
					magButton("Cancel", { variant: "ghost", onClick: () => finish(null) }),
					magButton("Save", {
						onClick: () => {
							const patch = collect();
							if (patch == null) return;
							finish(Object.keys(patch).length ? patch : null);
						},
					}),
				),
			),
		);
		document.body.appendChild(overlay);
		queueMicrotask(() => state.title?.input?.focus());
	});
}
